/**
 * The ProDesk API client.
 *
 * It is a thin layer over `session.authed` from `@fiveox/shell` — that is
 * where the bearer, the single serialized refresh and the one place a session
 * ends already live (see `session.ts`). What this file adds is the two things
 * the shell cannot know about: **the org header** and **this app's error
 * envelope**.
 */

import { SERVICE_API } from './config'
import { SELECTED_ORG_KEY, setSelectedOrgId } from './orgs'
import { session } from './session'

/**
 * A refusal with the machine code the service sent.
 *
 * The code is the contract: a screen switches on `SEAT_LIMIT_EXCEEDED` to say
 * "5 of 5 seats in use" and on `NOT_ENTITLED` to render the paywall. Throwing a
 * bare `Error` here is what produces "something went wrong".
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the refusal is about entitlement rather than about this user. */
  get isPaywall(): boolean {
    return this.code === 'NOT_ENTITLED' || this.code === 'USAGE_LIMIT_EXCEEDED'
  }

  get entitlementReason(): string | undefined {
    const reason = this.details.entitlement_reason
    return typeof reason === 'string' ? reason : undefined
  }
}

// -- the selected org --------------------------------------------------------
//
// A user belongs to many orgs and switches between them, so the selected org
// is **not** derivable from the token: the client sends it and the service
// proves the user may act in it (FiveOX ADR-0029).

let currentOrgId: string | null =
  typeof localStorage === 'undefined' ? null : localStorage.getItem(SELECTED_ORG_KEY)

const orgListeners = new Set<() => void>()

export function setOrgId(orgId: string | null): void {
  if (orgId === currentOrgId) return
  currentOrgId = orgId
  setSelectedOrgId(orgId)
  for (const listener of orgListeners) listener()
}

export const getOrgId = () => currentOrgId

export function subscribeOrg(listener: () => void): () => void {
  orgListeners.add(listener)
  return () => orgListeners.delete(listener)
}

// -- the fetcher -------------------------------------------------------------

/**
 * **`X-Fiveox-Org`** — the header the whole estate speaks. Not `X-Org-Id`:
 * prodesk-service invented that name and no core service reads it, so a core
 * caller sending the correct header would have been refused. Corrected
 * 2026-08-25.
 *
 * Renamed from `X-Fiveox-Org` by the FiveOX rebrand (ADR-0018). Emitters
 * send the new name only; prodesk-service and every core service still accept
 * the old one until the platform's Wave 6. **Every request that carries the
 * org uses this constant** — there is no second spelling of it in this app to
 * miss when the old name is finally dropped.
 */
export const ORG_HEADER = 'X-Fiveox-Org'

export interface Envelope<T> {
  success: boolean
  data: T
  meta?: Record<string, unknown>
}

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** For routes that are not org-scoped. */
  withoutOrg?: boolean
  /** A step-up assertion from core-mfa, for an action that cannot be undone. */
  stepUpAssertion?: string
}

export async function api<T>(path: string, options: Options = {}): Promise<Envelope<T>> {
  // **A plain object, not a `Headers` instance — and this is not a style
  // choice.** `@fiveox/shell`'s `authed` merges with
  // `{ ...(init.headers ?? {}), authorization: ... }`, and spreading a
  // `Headers` yields `{}` because it has no enumerable own properties. Passing
  // one silently drops every header except the bearer the shell adds itself.
  //
  // That cost an afternoon: the org header never left the browser, the
  // service refused with 400, the UI read it as `authority_unreachable` and
  // told the user the permission service was down. `content-type` was being
  // lost the same way, so every POST would have followed.
  //
  // Reported upstream — see docs/PLATFORM_INTEGRATION.md §9.3.
  const headers: Record<string, string> = {}
  for (const [key, value] of new Headers(options.headers).entries()) headers[key] = value
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (!options.withoutOrg) {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
  }
  if (options.stepUpAssertion) headers['X-StepUp-Assertion'] = options.stepUpAssertion

  // `session.authed` owns the bearer, the 401 refresh-and-replay, and ending
  // the session when the refresh comes back spent.
  const res = await session.authed(`${SERVICE_API}${path}`, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  const body = (await res.json().catch(() => null)) as
    | (Envelope<T> & {
        error?: { code: string; message: string; details?: Record<string, unknown> }
      })
    | null

  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'Something went wrong.',
      res.status,
      body?.error?.details ?? {},
    )
  }
  return body
}

// -- the module clients ------------------------------------------------------
//
// **This file holds no shapes and no endpoints.** The `ClientRecord` interface
// and the `clients` object lived here until Wave 2A and moved to
// `lib/api/clients.ts`; `lib/api/persons.ts` and `lib/api/firm.ts` are their
// siblings. One client per module (waves.md §1.1), because one file edited by
// nine sessions is nine merge conflicts in the file nobody wants a conflict in
// — this one, which owns the org header, the bearer and the error envelope for
// every call in the app.

// A new client goes in its own `lib/api/<module>.ts` and calls `api()` from
// here, so every request inherits the org header and this app's error
// envelope — docs/DOMAIN_HANDOFF.md §2. Do not grow this file.
